/**
 * Swamithra: the app update policy.
 *
 * Server-side configuration, per platform, that tells the Expo app whether its
 * installed build may keep running and whether a newer one is worth offering.
 * It lives on the server so the team can force or relax an update without an
 * app-store release.
 *
 * THE NUMBERS ARE BUILD NUMBERS, NOT VERSION NAMES: the Android `versionCode`
 * and the iOS `CFBundleVersion`. Both are plain integers that only ever go up,
 * so the comparison is `<` and never a semver parse.
 *
 *   minSupportedBuild  below it the app blocks until updated
 *   latestBuild        below it the app offers an update the user may decline
 *   remindAfterDays    null: offer once per version, then only a banner.
 *                      N: offer again N days after the user declined (0 means
 *                      every cold start). Lets the team change how often the
 *                      offer appears without shipping a build.
 *   storeUrl           where the "Update" button goes when the store must open.
 *                      Empty for a platform with no policy yet (both build
 *                      numbers 0): the server refuses to start with a build
 *                      number set and no URL, so an empty one is never needed.
 *                      Accepting it matters: refusing it would discard the
 *                      whole response, and with it the other platform's policy.
 */

import { z } from "zod"

/** How long a fetched policy is fresh. An hour: a release is not urgent to the minute. */
export const APP_VERSION_POLICY_TTL_MS = 60 * 60 * 1000

const buildNumber = z.number().int().nonnegative()

export const platformUpdatePolicySchema = z.object({
	minSupportedBuild: buildNumber,
	latestBuild: buildNumber,
	remindAfterDays: z.number().int().nonnegative().nullable(),
	storeUrl: z.string(),
})

export type PlatformUpdatePolicy = z.infer<typeof platformUpdatePolicySchema>

export const appVersionPolicySchema = z.object({
	android: platformUpdatePolicySchema,
	ios: platformUpdatePolicySchema,
})

export type AppVersionPolicy = z.infer<typeof appVersionPolicySchema>
